import { useEffect, useRef, useState } from 'react';
import { admiDocumentGenerationService } from '../admi-document-generation.service';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { OptionDto } from '../document-generation.dto';
import isEqual from 'lodash.isequal';
import { useDeepCompareEffect } from 'use-deep-compare';
import useDebouncer from 'src/common/useDebouncer';

const SEARCH_DEBOUNCE_MS = 300;

export function AsyncAutocompleteDropdown({
    fieldKey,
    label,
    required,
    value,
    onChange,
    dynamicKey,
    placeholder,
    options,
    onOptionsChange,
    dependencies,
}: {
    fieldKey: string;
    label: string;
    required?: boolean;
    value: any;
    onChange: (val: OptionDto['value'] | '') => void;
    dynamicKey: string;
    placeholder?: string;
    options?: OptionDto[];
    onOptionsChange: (options: OptionDto[]) => void;
    dependencies: Record<string, any>;
}) {
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState<OptionDto | null>(null);
    const [inputValue, setInputValue] = useState('');
    const debouncer = useDebouncer();
    // Responses can arrive out of order. Only the newest request may publish its
    // result, or a stale one for a shorter prefix overwrites the options the user
    // is actually looking at (GEN-759).
    const latestRequestRef = useRef(0);

    // No "skip if nothing changed" guard: useDeepCompareEffect already fires only when
    // dynamicKey or dependencies change, and onInputChange only fires when the user
    // types. A guard on top of those skipped the refetch when the field was cleared,
    // leaving the last search's options on screen instead of the default list.
    const fetchOptions = async (query = '', deps: Record<string, any> = {}) => {
        if (typeof query !== 'string') query = '';
        const requestId = ++latestRequestRef.current;
        setLoading(true);
        try {
            const response = await admiDocumentGenerationService.getFieldOptions(dynamicKey, query, deps);
            if (requestId !== latestRequestRef.current) return;
            onOptionsChange(response || []);
        } catch (error) {
            // already logged by the http-instance response interceptor; keep previous options on failure
            console.warn('Failed to fetch options for', dynamicKey, error);
        } finally {
            if (requestId === latestRequestRef.current) setLoading(false);
        }
    };

    useDeepCompareEffect(() => {
        fetchOptions('', dependencies);
    }, [dynamicKey, dependencies]);

    useEffect(() => {
        if (value != null && !isEqual(selectedOption?.value, value)) {
            const match = options?.find((opt) => isEqual(opt.value, value));
            if (match) setSelectedOption(match);
        }
    }, [value, options]);

    return (
        <Autocomplete
            key={fieldKey}
            options={options || []}
            loading={loading}
            value={selectedOption}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => isEqual(option.value, val?.value)}
            // The server has already matched the query; re-filtering here would hide
            // results whose label does not literally contain the typed string.
            filterOptions={(x) => x}
            onChange={(_, newOption) => {
                setSelectedOption(newOption || null);
                onChange(newOption?.value ?? '');
            }}
            onInputChange={(_, inputValue, reason) => {
                setInputValue(inputValue);
                if (reason === 'input') {
                    setLoading(true);
                    debouncer(() => fetchOptions(inputValue, dependencies), SEARCH_DEBOUNCE_MS);
                }
            }}
            renderOption={({ key, ...optionProps }, option) => (
                <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: '100%',
                        py: 1,
                        px: 2,
                    }}
                >
                    {typeof option.value !== 'object' || option.value === null ? (
                        <Typography variant="body2" sx={{ width: '100%', textAlign: 'left' }}>
                            {String(option.value)}
                        </Typography>
                    ) : (
                        Object.entries(option.value).map(([key, val]) => (
                            <Typography
                                key={key}
                                variant="body2"
                                sx={{ width: '100%', textAlign: 'left' }} // Ensure text is left-aligned
                            >
                                <Box component="span" sx={{ fontWeight: 'bold' }}>
                                    {key}:
                                </Box>{' '}
                                {String(val)}
                            </Typography>
                        ))
                    )}
                </Box>
            )}
            loadingText="Loading options..."
            noOptionsText={inputValue.trim().length === 1 ? 'Keep typing to search…' : 'No matches found'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required={required}
                />
            )}
        />
    );
}
